import { useState } from 'react';
import {
  Button,
  Card,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  toast,
} from '@ludolio/shared-ui';
import { getErrorMessage, labsApi } from '@/lib/api';
import { VISIBILITY_OPTIONS, formatDate } from '@/lib/format';
import type { LabProjectDetail, LabRelease, LabVisibility } from '@/lib/types';

const RANK: Record<LabVisibility, number> = { private: 0, unit: 1, organization: 2 };

export function PublishedPanel({
  release,
  hasUnit,
  onChange,
}: {
  release: LabRelease;
  hasUnit: boolean;
  onChange: (detail: LabProjectDetail) => void;
}) {
  const [busy, setBusy] = useState(false);
  const options = VISIBILITY_OPTIONS.filter((option) =>
    RANK[option.value] <= RANK[release.visibility] && (option.value !== 'unit' || hasUnit));

  const stopRemixing = async () => {
    setBusy(true);
    try {
      onChange(await labsApi.stopRemixing(release.id));
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const narrow = async (visibility: LabVisibility) => {
    if (visibility === release.visibility) return;
    setBusy(true);
    try {
      onChange(await labsApi.narrowVisibility(release.id, visibility));
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="grid gap-4 p-4 sm:grid-cols-[minmax(0,1fr)_18rem] sm:items-center">
      <div className="min-w-0">
        <p className="font-medium">Live version {release.version}</p>
        <p className="text-sm text-muted-foreground">Published {formatDate(release.publishedAt)}</p>
      </div>
      <div className="flex flex-col gap-2">
        <Select value={release.visibility} onValueChange={(value) => void narrow(value as LabVisibility)} disabled={busy}>
          <SelectTrigger aria-label="Who can play the live version">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {options.map((option) => (
              <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {release.remixPermission === 'organization' && (
          <Button type="button" variant="outline" className="w-full" disabled={busy} onClick={() => void stopRemixing()}>
            Stop remixing
          </Button>
        )}
      </div>
    </Card>
  );
}
